<template>
    <h2 class="price-details__header">{{ t('projectclient.projectinformation.price.details') }}</h2>
    <div v-if="priceSummary.shouldDisplayFees" class="price-details__wrapper">
        <p>{{ t('projectclient.projectinformation.price.subtotal.excltax') }}</p>
        <p class="price-details__value">
            {{ handleCurrency(priceSummary.amountWithoutFeesAndTaxes, currency, locale) }}
        </p>
    </div>
    <div v-if="priceSummary.shouldDisplayFees" class="price-details__wrapper">
        <p>{{ t('projectclient.projectinformation.price.fees.excltax') }}</p>
        <p class="price-details__value">
            {{ handleCurrency(priceSummary.serviceFeesWithoutTaxes, currency, locale) }}
        </p>
    </div>
    <div class="price-details__wrapper">
        <p class="bold">{{ t('projectclient.projectinformation.price.total.excltax') }}</p>
        <p class="price-details__value bold">
            {{ handleCurrency(priceSummary.amountWithoutTaxes, currency, locale) }}
        </p>
    </div>
    <div class="price-details__wrapper">
        <p>{{ t('projectclient.projectinformation.price.taxes') }}</p>
        <p class="price-details__value">
            {{ handleCurrency(priceSummary.taxes, currency, locale) }}
        </p>
    </div>
    <div class="price-details__wrapper price-details__total">
        <p class="bold">{{ t('projectclient.projectinformation.price.amount.incltax') }}</p>
        <p class="price-details__value bold">
            {{ handleCurrency(priceSummary.amountTotalWithFeesAndTaxes, currency, locale) }}
        </p>
    </div>
</template>

<script setup lang="ts">
    import {handleCurrency} from '@common/utils';
    import {storeToRefs} from 'pinia';
    import {useTranslation, useLocale, useRoute, useAsyncData} from '#imports';
    import type {ContractResourceCurrencyEnum} from '@api';
    import {useExpenseValidationStore} from '../../stores/expense-validation.store';

    const {t} = useTranslation();
    const {query} = useRoute();
    const {locale} = useLocale();

    const props = defineProps<{
        projectId: string;
        currency: ContractResourceCurrencyEnum;
    }>();

    // expenseValidation store
    const expenseValidationStore = useExpenseValidationStore();
    const {priceSummary} = storeToRefs(expenseValidationStore);

    useAsyncData(
        async () => {
            if (query.batch) {
                await expenseValidationStore.fetchExpenseBatch({
                    projectId: props.projectId,
                    expenseBatchId: query.batch as string,
                });
            }
        },
        {deep: true},
    );
</script>

<style lang="scss" scoped>
    .price-details {
        &__header {
            font-weight: var(--joy-font-weight-bold);
            font-size: var(--joy-font-size-primary-450);
            line-height: var(--joy-line-height-large);
        }

        &__wrapper {
            display: flex;
            justify-content: space-between;
            font-weight: var(--joy-font-weight-normal);
            font-size: var(--joy-font-size-primary-300);
            line-height: var(--joy-line-height-large);

            p {
                margin-bottom: 10px;
            }
        }

        &__value {
            font-weight: var(--joy-font-weight-normal);
            font-size: var(--joy-font-size-primary-300);
        }

        &__total {
            padding: var(--joy-core-spacing-3);
            background: var(--joy-color-secondary-10);
            border-radius: var(--joy-core-radius-2);
            color: var(--joy-color-secondary-50);
            font-weight: var(--joy-font-weight-bold);
            margin-left: -12px !important;
            margin-right: -12px !important;

            p {
                margin: 0;
            }
        }
    }

    .bold {
        font-weight: var(--joy-font-weight-bold);
    }
</style>
